import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, TrendingDown, Filter, Search, Trash2, Calendar, ScanLine, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from '../hooks/use-toast';
import { expenseAPI, aiAPI, recurringAPI, incomeAPI, portfolioAPI, reminderAPI } from '../services/apiServices';
import { useCachedApi } from '../hooks/useCachedApi';
import { useCache } from '../contexts/CacheContext';

// --- Custom CSS for Classy + Glassy Pro V2 ---
const customStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

  body {
    font-family: 'Plus Jakarta Sans', sans-serif;
  }

  /* Nebula Background */
  .nebula-bg {
    background-color: #020617;
    background-image: 
      radial-gradient(circle at 15% 50%, rgba(76, 29, 149, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 85% 30%, rgba(56, 189, 248, 0.1) 0%, transparent 50%);
  }

  /* Premium Glass Cards */
  .glass-card {
    background: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.05);
    box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.3);
  }

  /* List Items Glass Strip */
  .glass-strip {
    background: rgba(255, 255, 255, 0.02);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .glass-strip:hover {
    background: rgba(255, 255, 255, 0.08); /* White/10 approx */
    transform: scale(1.01);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    z-index: 10;
  }

  /* Custom Scrollbar */
  .custom-scrollbar::-webkit-scrollbar { width: 6px; }
  .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(51, 65, 85, 0.5); border-radius: 10px; }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(71, 85, 105, 0.8); }

  /* Staggered Animation */
  @keyframes slideUpFade {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .stagger-item {
    opacity: 0;
    animation: slideUpFade 0.5s ease-out forwards;
  }

  @keyframes pulse-rate {
    0%, 100% { height: 4px; opacity: 0.2; }
    50% { height: 32px; opacity: 1; }
  }
  .pulse-bar {
    width: 3px;
    background: #10b981;
    border-radius: 99px;
    animation: pulse-rate 1s infinite ease-in-out;
  }
`;

// Helper for 3D Tilt Card
const TiltCard = ({ children, className }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Calculate rotation (max 10deg)
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -10;
    const rotateY = ((x - centerX) / centerX) * 10;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
  };

  return (
    <div
      ref={cardRef}
      className={`transition-transform duration-200 ease-out will-change-transform ${className}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </div>
  );
};

// Reusable Switch Component
const Switch = ({ checked, onCheckedChange }) => (
  <button
    type="button"
    onClick={() => onCheckedChange(!checked)}
    className={`w-10 h-5 rounded-full relative transition-colors ${checked ? 'bg-cyan-500' : 'bg-slate-700'}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
  </button>
);

const allCategories = [
  'Food', 'Transport', 'Shopping', 'Bills',
  'Entertainment', 'Health', 'Education',
  'Groceries', 'Rent', 'Utilities', 'Salary',
  'Investment', 'Fuel', 'EMI', 'Travel', 'Others'
];

const Expenses = () => {
  const navigate = useNavigate();
  const { invalidateCache } = useCache();

  // Data Fetching
  const {
    data: expenses = [],
    loading: loadingExpenses,
    refetch: refetchExpenses,
    isFromCache: expensesFromCache
  } = useCachedApi(expenseAPI.getExpenses, 'expenses', {
    onError: (e) => toast({ title: 'Error', description: 'Failed to load expenses.' })
  });

  const { data: incomes = [] } = useCachedApi(incomeAPI.getIncome, 'income', { onError: () => [] });
  const { data: portfolio = [] } = useCachedApi(portfolioAPI.getPortfolio, 'portfolio', { onError: () => [] });
  const { data: reminders = [] } = useCachedApi(reminderAPI.getReminders, 'reminders', { onError: () => [] });

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showRangeFilter, setShowRangeFilter] = useState(false);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isRecurringDialogOpen, setIsRecurringDialogOpen] = useState(false);
  const [isScanning, setIsScanning] = useState(false);

  const [recurringList, setRecurringList] = useState([]);
  const [savingsSuggestions, setSavingsSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [modelsExhausted, setModelsExhausted] = useState(false);

  const fileInputRef = useRef(null);

  const [newExpense, setNewExpense] = useState({
    description: '', amount: '', category: 'Food', date: new Date().toISOString().split('T')[0],
    isRecurring: false, frequency: 'monthly', duration_months: '', source: 'Manual'
  });

  // Effects
  useEffect(() => {
    recurringAPI.processDueRecurring().then(() => {
      invalidateCache('expenses');
      refetchExpenses();
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Fetch suggestions if we don't have them yet
    if (savingsSuggestions.length === 0 && !loadingSuggestions && !modelsExhausted && expenses.length > 0) {
      console.log("[AI Insights] Triggering deep analysis...");
      setLoadingSuggestions(true);
      aiAPI.getFinancialInsights({
        expenses,
        incomes,
        portfolio,
        reminders
      }).then(data => {
        if (data && data.length > 0) {
          setSavingsSuggestions(data);
        }
        setLoadingSuggestions(false);
      }).catch((err) => {
        console.warn("[AI Insights] Fallback chain exhausted:", err);
        setModelsExhausted(true);
        setLoadingSuggestions(false);
      });
    }
  }, [expenses?.length, incomes.length, portfolio.length, reminders.length, savingsSuggestions.length, loadingSuggestions, modelsExhausted]);

  // Handlers
  const fetchRecurring = async () => {
    const data = await recurringAPI.getRecurring();
    // Filter out income-type recurring rules in the Expenses view
    setRecurringList(data?.filter(r => r.type !== 'income') || []);
  };

  const handleScanClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsScanning(true);
    try {
      const scannedData = await aiAPI.scanReceipt(file);
      if (!scannedData || !scannedData.amount) throw new Error("Could not extract valid data from receipt.");

      setNewExpense(prev => ({
        ...prev,
        amount: scannedData.amount,
        date: scannedData.date || new Date().toISOString().split('T')[0],
        description: scannedData.description || scannedData.merchant || "Scanned Receipt",
        category: scannedData.category || "Others",
        source: 'Scanned'
      }));
      setIsAddDialogOpen(true);
      toast({ title: "Receipt Scanned", description: "AI successfully extracted transaction details." });
    } catch (error) {
      toast({ title: "Scan Failed", description: error.message, variant: "destructive" });
    } finally {
      setIsScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      if (!newExpense.amount || !newExpense.description) return toast({ title: "Missing Info", description: "Please fill all fields." });

      // Separate payload for expenses table (exclude recurring-only fields)
      const { isRecurring, frequency, duration_months, ...expenseData } = newExpense;

      await expenseAPI.addExpense({
        ...expenseData,
        amount: parseFloat(newExpense.amount),
        source: newExpense.source
      });

      // Handle Recurring via separate API
      if (newExpense.isRecurring) {
        await recurringAPI.createRecurring({
          description: newExpense.description,
          amount: parseFloat(newExpense.amount),
          category: newExpense.category,
          frequency: newExpense.frequency,
          start_date: newExpense.date,
          duration_months: newExpense.duration_months ? parseInt(newExpense.duration_months) : null
        });
      }

      toast({ title: "Success", description: "Expense added successfully." });
      setIsAddDialogOpen(false);

      // Reset Form
      setNewExpense({
        description: '', amount: '', category: 'Food', date: new Date().toISOString().split('T')[0],
        isRecurring: false, frequency: 'monthly', duration_months: '', source: 'Manual'
      });

      invalidateCache('expenses');
      refetchExpenses();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleDeleteExpense = async (id) => {
    try {
      await expenseAPI.deleteExpense(id);
      toast({ title: "Deleted", description: "Expense removed." });
      invalidateCache('expenses');
      refetchExpenses();
    } catch (e) {
      toast({ title: "Error", description: "Failed to delete.", variant: "destructive" });
    }
  };

  const handleToggleRecurring = async (id, isActive) => {
    await recurringAPI.toggleRecurring(id, isActive);
    fetchRecurring();
  };

  const handleDeleteRecurring = async (id) => {
    await recurringAPI.deleteRecurring(id);
    fetchRecurring();
  };

  // Calculations
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  // Filtering logic
  const filteredExpenses = (expenses || []).filter(exp => {
    const matchesSearch = exp.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exp.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || exp.category === filterCategory;

    let matchesDate = true;
    if (dateRange.start || dateRange.end) {
      if (dateRange.start) matchesDate = matchesDate && new Date(exp.date) >= new Date(dateRange.start);
      if (dateRange.end) matchesDate = matchesDate && new Date(exp.date) <= new Date(dateRange.end);
    } else {
      // Default: Current Month
      const d = new Date(exp.date);
      const now = new Date();
      matchesDate = d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }
    return matchesSearch && matchesCategory && matchesDate;
  });

  const filteredTotal = filteredExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const totalExpenses = (expenses || []).reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

  const getCategoryColor = (cat) => {
    const colors = {
      Food: '#EF4444', Transport: '#3B82F6', Shopping: '#F59E0B', Bills: '#10B981',
      Entertainment: '#8B5CF6', Education: '#EC4899', Health: '#06B6D4',
      Groceries: '#14B8A6', Rent: '#6366F1', Utilities: '#8B5CF6', Salary: '#22C55E',
      Investment: '#EAB308', Fuel: '#F97316', EMI: '#D946EF', Travel: '#0EA5E9', Others: '#94A3B8'
    };
    return colors[cat] || '#64748B';
  };

  return (
    <DashboardLayout>
      <style>{customStyles}</style>
      <div className="flex flex-col h-full nebula-bg text-slate-200 p-6 md:p-8 overflow-y-auto custom-scrollbar">

        {/* Header Title & Primary Action */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in slide-in-from-left-6 duration-1000">
          <div>
            <div className="mb-3 flex items-center gap-2 bg-emerald-500/5 px-2 py-0.5 rounded-full border border-emerald-500/10 w-fit">
              <Sparkles size={10} className="text-emerald-400" />
              <span className="text-[8px] font-black uppercase tracking-[0.3em] text-emerald-400">FinAssure Intelligence Unit</span>
            </div>
            <h1 className="text-xl md:text-3xl font-black tracking-tighter text-white uppercase leading-none mb-3">
              EXPENSE <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-500">TRACKER</span>
            </h1>
            <p className="text-slate-400 text-[13px] max-w-2xl font-medium leading-relaxed">
              Institutional-grade monitoring and AI-driven optimization of your personal capital outflows.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} accept="image/*" />

            <Button
              onClick={handleScanClick}
              disabled={isScanning}
              className="rounded-full h-12 px-6 bg-slate-900/60 hover:bg-slate-800 border border-purple-500/30 text-purple-400 hover:text-purple-300 transition-all font-black text-[10px] uppercase tracking-widest shadow-xl"
            >
              {isScanning ? <Loader2 className="animate-spin mr-2" size={16} /> : <ScanLine size={16} className="mr-2" />}
              AI SCAN
            </Button>

            <Button
              variant="ghost"
              onClick={() => { fetchRecurring(); setIsRecurringDialogOpen(true); }}
              className="rounded-full h-12 px-6 bg-slate-900/60 hover:bg-slate-800 border border-cyan-500/30 text-cyan-400 hover:text-cyan-300 transition-all font-black text-[10px] uppercase tracking-widest shadow-xl"
            >
              <RefreshCw size={16} className="mr-2" />
              RECURRING
            </Button>

            <Button
              onClick={() => setIsAddDialogOpen(true)}
              className="rounded-full h-12 px-8 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-black shadow-lg shadow-blue-500/25 border-0 hover:scale-105 transition-transform uppercase tracking-widest text-[11px]"
            >
              <Plus size={18} className="mr-2" /> ADD EXPENSE
            </Button>
          </div>
        </div>

        {/* 1. Top Glass Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8">
          {/* Filtered Expenses Card (Fitted with Range Filter) */}
          <div className="glass-card rounded-2xl p-4 md:p-6 relative overflow-hidden group min-h-[160px] flex flex-col justify-between">
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-red-500 opacity-60 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-orange-500/10 rounded-full blur-3xl group-hover:bg-orange-500/20 transition-all"></div>
            <div className="relative z-10 flex justify-between items-start gap-4">
              <div className="flex-1">
                <p className="text-slate-400 font-medium text-sm mb-2 uppercase tracking-wider">
                  {filterCategory === 'all' ? 'Filtered Total' : `${filterCategory} Total`}
                </p>
                <div className="text-white text-2xl font-bold tracking-tight mb-2">
                  ₹{Number(filteredTotal).toLocaleString('en-IN')}
                </div>

                {showRangeFilter ? (
                  <div className="mt-4 flex flex-col gap-3 animate-in fade-in slide-in-from-top-2">
                    <div className="flex items-center gap-3 bg-black/20 p-2 rounded-xl border border-white/5">
                      <Input
                        type="date"
                        value={dateRange.start}
                        onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                        className="h-8 bg-slate-900/80 border-slate-700 text-[10px] w-full text-white cursor-pointer"
                      />
                      <span className="text-slate-500 text-[8px] font-black uppercase tracking-tighter">to</span>
                      <Input
                        type="date"
                        value={dateRange.end}
                        onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                        className="h-8 bg-slate-900/80 border-slate-700 text-[10px] w-full text-white cursor-pointer"
                      />
                    </div>
                    <button
                      onClick={() => {
                        setDateRange({ start: '', end: '' });
                        setShowRangeFilter(false);
                      }}
                      className="text-[9px] text-orange-400 hover:text-orange-300 font-black uppercase tracking-[0.2em] w-fit px-2 py-1 rounded bg-orange-500/5 hover:bg-orange-500/10 transition-colors"
                    >
                      Reset to Current Month
                    </button>
                  </div>
                ) : (
                  <p className="mt-3 text-slate-500 text-xs font-medium bg-slate-800/50 inline-block px-2 py-1 rounded-lg border border-slate-700/50">
                    {dateRange.start || dateRange.end
                      ? `${dateRange.start || '...'} → ${dateRange.end || '...'}`
                      : `${new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}`}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={() => setShowRangeFilter(!showRangeFilter)}
                  className={`w-12 h-12 rounded-xl flex items-center justify-center border transition-all shadow-[0_0_15px_rgba(249,115,22,0.2)] flex-shrink-0 ${showRangeFilter ? 'bg-orange-500 text-white border-orange-400' : 'bg-gradient-to-br from-orange-500/10 to-red-500/10 border-orange-500/20 text-orange-400 hover:bg-orange-500/20'}`}
                >
                  <Calendar size={20} />
                </button>
                <span className="text-[11px] font-black uppercase tracking-[0.2em] text-orange-400/80 text-center">Range<br />Filter</span>
              </div>
            </div>
          </div>

          {/* Lifetime Card */}
          <div className="glass-card rounded-2xl p-4 md:p-6 relative overflow-hidden group">
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-pink-500 opacity-60 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-purple-500/10 rounded-full blur-3xl group-hover:bg-purple-500/20 transition-all"></div>
            <div className="relative z-10 flex justify-between items-start">
              <div>
                <p className="text-slate-400 font-medium text-sm mb-2">Total Lifetime Expenses</p>
                <div className="text-white text-2xl font-bold tracking-tight">₹{Number(totalExpenses).toLocaleString('en-IN')}</div>
                <p className="text-slate-500 text-xs mt-2 font-medium bg-slate-800/50 inline-block px-2 py-1 rounded-lg border border-slate-700/50">All Time</p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={() => navigate('/expense-history')}
                  className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 flex items-center justify-center border border-purple-500/20 text-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.2)] hover:bg-purple-500/20 transition-all cursor-pointer group-hover:scale-105"
                >
                  <TrendingDown size={20} className="group-hover:-rotate-45 transition-transform" />
                </button>
                <span className="text-[11px] font-black uppercase tracking-[0.2em] text-purple-400/80 text-center">View<br />History</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">

          {/* Main Content Area (Left 2 cols) */}
          <div className="lg:col-span-2 space-y-6">

            {/* 3. Action Bar (Separated) */}
            <div className="glass-card p-3 md:p-4 rounded-2xl flex flex-col gap-4">

              {/* Row 1: Filters */}
              <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
                {/* Search Pill */}
                <div className="relative w-full md:w-1/3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full bg-slate-900/80 border border-slate-700 text-slate-200 text-sm rounded-full pl-10 pr-4 h-10 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all"
                  />
                </div>

                <div className="flex gap-2 w-full md:w-auto">
                  {/* Category Filter */}
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="bg-slate-900/80 border-slate-700 text-slate-300 rounded-full h-10 text-xs w-40">
                      <Filter size={14} className="mr-2 text-blue-400" />
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700 text-slate-300">
                      <SelectItem value="all">All Categories</SelectItem>
                      {allCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

            </div>

            {/* 4. Glass Strip Expenses List */}
            <div className="glass-card rounded-2xl p-4 min-h-[400px]">
              <div className="space-y-2">
                {filteredExpenses.slice(0, 15).map((expense, i) => {
                  const color = getCategoryColor(expense.category);
                  return (
                    <div
                      key={expense.id}
                      className="glass-strip rounded-xl p-3 md:p-4 flex items-center justify-between group stagger-item"
                      style={{ animationDelay: `${i * 50}ms` }}
                    >
                      <div className="flex items-center gap-4">
                        {/* Neon Icon */}
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center text-lg shadow-lg"
                          style={{ backgroundColor: `${color}15`, color: color, border: `1px solid ${color}30` }}
                        >
                          {expense.category === 'Food' ? '🍔' : expense.category === 'Transport' ? '🚕' :
                            expense.category === 'Shopping' ? '🛍️' : expense.category === 'Bills' ? '🧾' : '📦'}
                        </div>

                        <div>
                          <p className="text-white font-medium text-sm">{expense.description}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[11px] text-slate-400 font-mono tracking-wide uppercase">{new Date(expense.date).toLocaleDateString()}</span>
                            <span
                              className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider border ${expense.source === 'Scanned'
                                ? 'bg-purple-900/30 text-purple-400 border-purple-500/30'
                                : 'bg-slate-800 text-slate-400 border-slate-700'
                                }`}
                            >
                              {expense.source === 'Scanned' ? 'AI Scan' : 'Manual'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        <span className="text-white font-bold text-lg tracking-tight group-hover:text-red-400 transition-colors">
                          -₹{Number(expense.amount).toLocaleString('en-IN')}
                        </span>
                        <button
                          onClick={() => handleDeleteExpense(expense.id)}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-all opacity-0 group-hover:opacity-100"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {filteredExpenses.length === 0 && (
                  <div className="text-center py-20 text-slate-500">
                    <p className="text-sm">No expenses found matching filters.</p>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Right Sidebar - AI Actions */}
          <div className="lg:col-span-1 space-y-6">

            {/* 5. AI 3D Tilt Cards */}
            <div className="glass-card p-6 rounded-2xl sticky top-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                  AI Insights
                </h2>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={loadingSuggestions}
                  onClick={() => {
                    setSavingsSuggestions([]);
                    setLoadingSuggestions(true);
                    setModelsExhausted(false);
                    aiAPI.getFinancialInsights({
                      expenses,
                      incomes,
                      portfolio,
                      reminders
                    }).then(data => {
                      if (data && data.length > 0) {
                        setSavingsSuggestions(data);
                      }
                      setLoadingSuggestions(false);
                    }).catch(() => {
                      setModelsExhausted(true);
                      setLoadingSuggestions(false);
                    });
                  }}
                >
                  <RefreshCw size={14} className={`text-slate-500 ${loadingSuggestions ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              <div className="space-y-4">


                {(loadingSuggestions || modelsExhausted || !savingsSuggestions.length) ? (
                  <div className="flex flex-col items-center justify-center p-10 bg-slate-900/30 rounded-[2rem] border border-dashed border-slate-800/50 backdrop-blur-sm animate-in fade-in duration-500">
                    <div className="flex items-end gap-1.5 h-12 mb-6">
                      <div className={`pulse-bar ${loadingSuggestions ? 'bg-blue-500' : modelsExhausted ? 'bg-orange-500' : 'bg-slate-700'}`} style={{ animationDelay: '0.1s' }}></div>
                      <div className={`pulse-bar ${loadingSuggestions ? 'bg-blue-500' : modelsExhausted ? 'bg-orange-500' : 'bg-slate-700'}`} style={{ animationDelay: '0.3s' }}></div>
                      <div className={`pulse-bar ${loadingSuggestions ? 'bg-blue-500' : modelsExhausted ? 'bg-orange-500' : 'bg-slate-700'}`} style={{ animationDelay: '0.5s' }}></div>
                      <div className={`pulse-bar ${loadingSuggestions ? 'bg-blue-500' : modelsExhausted ? 'bg-orange-500' : 'bg-slate-700'}`} style={{ animationDelay: '0.2s' }}></div>
                      <div className={`pulse-bar ${loadingSuggestions ? 'bg-blue-500' : modelsExhausted ? 'bg-orange-500' : 'bg-slate-700'}`} style={{ animationDelay: '0.4s' }}></div>
                    </div>

                    <p className={`text-[11px] font-black uppercase tracking-[0.3em] mb-2 ${loadingSuggestions ? 'text-blue-400' : modelsExhausted ? 'text-orange-400' : 'text-slate-400'}`}>
                      {loadingSuggestions ? 'Neural Link Establishing' : modelsExhausted ? 'Cognitive Link Timeout' : 'Standby Mode'}
                    </p>

                    <p className="text-slate-400 text-[11px] text-center leading-relaxed">
                      {loadingSuggestions
                        ? 'Connecting to high-frequency intelligence models...'
                        : modelsExhausted
                          ? 'Models are currently recalibrating. Please try again in 1 min.'
                          : 'Add more expense records to activate archival pattern recognition.'}
                    </p>
                  </div>
                ) : (
                  savingsSuggestions.map((s, i) => s && (
                    <TiltCard key={i} className="glass-card bg-slate-900/80 p-5 rounded-xl border border-slate-700 hover:border-emerald-500/50 group relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity" />

                      <div className="flex justify-between items-start mb-2">
                        <span className="text-emerald-400 text-xs font-bold uppercase tracking-wider">{s.category}</span>
                        <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-500/20 group-hover:bg-emerald-500 group-hover:text-black transition-colors">
                          Save ₹{s.potentialSavings}
                        </span>
                      </div>

                      <p className="description-text text-slate-300 text-xs leading-relaxed mb-3">{s.description}</p>
                      <p className="text-slate-400 text-[11px] italic border-l border-emerald-500/30 pl-3 mb-3">{s.tip}</p>

                      <div className="flex justify-between items-center text-[10px] text-slate-500">
                        <span>Target: <span className="text-white">₹{s.suggestedSpending}</span></span>
                        <div className="h-1 w-12 bg-slate-800 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 w-3/4"></div>
                        </div>
                      </div>
                    </TiltCard>
                  ))
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="bg-black border-white/10 text-white max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden p-0">
          <div className="p-8 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
            <DialogTitle className="text-xl font-black text-white tracking-tighter italic uppercase">Track Expenditure</DialogTitle>
          </div>
          <form onSubmit={handleAddExpense} className="p-8 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 tracking-widest ml-1 uppercase italic">Date</label>
                <Input type="date" value={newExpense.date} onChange={e => setNewExpense({ ...newExpense, date: e.target.value })} className="h-12 bg-zinc-950 border-white/10 rounded-2xl text-white focus:ring-blue-500/50" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 tracking-widest ml-1 uppercase italic">Amount (₹)</label>
                <Input type="number" placeholder="0.00" value={newExpense.amount} onChange={e => setNewExpense({ ...newExpense, amount: e.target.value })} className="h-12 bg-zinc-950 border-white/10 rounded-2xl text-white placeholder:text-slate-700 font-black" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 tracking-widest ml-1 uppercase italic">Description</label>
              <Input placeholder="e.g. Groceries" value={newExpense.description} onChange={e => setNewExpense({ ...newExpense, description: e.target.value })} className="h-12 bg-zinc-950 border-white/10 rounded-2xl text-white placeholder:text-slate-700 font-bold" />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 tracking-widest ml-1 uppercase italic">Category</label>
              <Select value={newExpense.category} onValueChange={v => setNewExpense({ ...newExpense, category: v })}>
                <SelectTrigger className="h-12 bg-zinc-950 border-white/10 rounded-2xl text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10 text-white">
                  {allCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-4 bg-blue-500/5 border border-blue-500/10 rounded-2xl">
              <div className="flex items-center gap-2">
                <RefreshCw size={14} className="text-blue-400" />
                <span className="text-[11px] font-black text-blue-400 uppercase tracking-widest italic">Automate bill?</span>
              </div>
              <Switch checked={newExpense.isRecurring} onCheckedChange={c => setNewExpense({ ...newExpense, isRecurring: c })} />
            </div>

            {newExpense.isRecurring && (
              <div className="grid grid-cols-2 gap-4 animate-in slide-in-from-top-2 duration-300">
                <Select value={newExpense.frequency} onValueChange={v => setNewExpense({ ...newExpense, frequency: v })}>
                  <SelectTrigger className="h-12 bg-zinc-950 border-white/10 rounded-2xl text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-white/10 text-white">
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Months" type="number" value={newExpense.duration_months} onChange={e => setNewExpense({ ...newExpense, duration_months: e.target.value })} className="h-12 bg-zinc-950 border-white/10 rounded-2xl text-white placeholder:text-slate-700" />
              </div>
            )}

            <Button type="submit" className="w-full h-14 bg-gradient-to-r from-blue-700 to-blue-900 hover:from-blue-600 hover:to-blue-800 text-white font-black py-5 rounded-2xl mt-4 tracking-widest shadow-lg shadow-blue-500/10 text-xs italic uppercase">
              Save Expense Entry
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Recurring Dialog */}
      <Dialog open={isRecurringDialogOpen} onOpenChange={setIsRecurringDialogOpen}>
        <DialogContent className="bg-black border-slate-800 text-white max-w-2xl">
          <DialogHeader className="flex flex-row items-center justify-between border-b border-white/5 pb-4">
            <DialogTitle className="text-xl font-black uppercase tracking-tighter">Expense Automation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-6">
            <Button
              onClick={() => {
                setIsRecurringDialogOpen(false);
                setNewExpense(prev => ({ ...prev, isRecurring: true }));
                setIsAddDialogOpen(true);
              }}
              className="w-full h-14 bg-black hover:bg-sky-400 border border-cyan-500/40 text-cyan-400 hover:text-black font-black rounded-xl flex items-center justify-center gap-2 transition-all duration-300 shadow-[0_0_20px_rgba(34,211,238,0.1)] hover:shadow-sky-500/25 uppercase tracking-widest text-[11px]"
            >
              <Plus size={18} /> ADD RECURRING TRANSACTION
            </Button>

            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
              {recurringList.map(r => (
                <div key={r.id} className="flex justify-between items-center p-4 bg-zinc-950/50 hover:bg-sky-500/5 rounded-2xl border border-white/5 hover:border-sky-500/20 transition-all group">
                  <div>
                    <p className="font-bold text-slate-100">{r.description}</p>
                    <p className="text-slate-400 text-[11px] uppercase font-black tracking-widest mt-1">{r.frequency} • ₹{r.amount}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Switch checked={r.is_active} onCheckedChange={c => handleToggleRecurring(r.id, c)} />
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteRecurring(r.id)} className="text-slate-600 hover:text-rose-500 hover:bg-rose-500/10 transition-colors">
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
              {recurringList.length === 0 && (
                <div className="text-center py-12 border border-dashed border-white/5 rounded-2xl">
                  <p className="text-slate-600 font-bold text-xs uppercase tracking-widest">No Active Automation Rules</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  );
};

export default Expenses;